import { useEffect, useRef } from 'react';
import { clearPrefetchCache } from '../utils/tabPrefetch';

const sockets = {};
const subscribers = {};
let lastWakeUpTime = 0; // Глобальный антиспам таймер

const useTournamentSocket = (tournamentId, onMessage) => {
    const savedCallback = useRef();

    useEffect(() => {
        savedCallback.current = onMessage;
    }, [onMessage]);

    const connectSocket = () => {
        if (!tournamentId) return;

        let ws = sockets[tournamentId];
        if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
            return;
        }

        const host = process.env.NODE_ENV === 'development' ? '127.0.0.1:8000' : window.location.host;
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        ws = new WebSocket(`${protocol}//${host}/ws/tournaments/${tournamentId}/`);
        sockets[tournamentId] = ws;

        ws.onmessage = (event) => {
            try {
                const payload = JSON.parse(event.data);
                clearPrefetchCache();
                subscribers[tournamentId]?.forEach(cb => {
                    if (cb?.current) {
                        // БРОНЕЖИЛЕТ ОТ СЕРОГО ЭКРАНА:
                        // Ловим ошибки рендера внутри компонентов, чтобы React не убил всё приложение
                        try {
                            if (payload.type === 'match_update') {
                                cb.current(payload.data);
                            } else {
                                cb.current(payload);
                            }
                        } catch (reactErr) {
                            console.error("React crash prevented! UI is safe.", reactErr);
                        }
                    }
                });
            } catch (err) {}
        };

        ws.onclose = () => {
            if (sockets[tournamentId] === ws) {
                sockets[tournamentId] = null;
            }
        };
    };

    // ЭЛЕГАНТНОЕ ПРОБУЖДЕНИЕ С ЗАЩИТОЙ
    useEffect(() => {
        if (typeof document === 'undefined' || !tournamentId) return;

        const handleWakeUp = () => {
            if (document.visibilityState === 'hidden') return;

            // АНТИСПАМ: Защита от моргающего Wi-Fi (реагируем не чаще 1 раза в 3 секунды)
            if (Date.now() - lastWakeUpTime < 3000) return;
            lastWakeUpTime = Date.now();

            clearPrefetchCache();

            // 1. Говорим React-компонентам подтянуть свежие данные
            subscribers[tournamentId]?.forEach(cb => {
                if (cb?.current) {
                    try { cb.current({ wake_up: true }); } catch(e) {}
                }
            });

            // 2. Убиваем старый сокет-зомби
            const oldWs = sockets[tournamentId];
            if (oldWs) {
                oldWs.onclose = null;
                oldWs.close();
            }

            // 3. Жестко стартуем новый коннект
            sockets[tournamentId] = null;
            connectSocket();
        };

        document.addEventListener('visibilitychange', handleWakeUp);
        window.addEventListener('online', handleWakeUp);

        return () => {
            document.removeEventListener('visibilitychange', handleWakeUp);
            window.removeEventListener('online', handleWakeUp);
        };
    }, [tournamentId]);

    // Базовый запуск сокета и отписка
    useEffect(() => {
        if (!tournamentId) return;
        if (!subscribers[tournamentId]) subscribers[tournamentId] = [];
        subscribers[tournamentId].push(savedCallback);

        connectSocket();

        return () => {
            subscribers[tournamentId] = subscribers[tournamentId].filter(cb => cb !== savedCallback);
            setTimeout(() => {
                if (subscribers[tournamentId] && subscribers[tournamentId].length === 0 && sockets[tournamentId]) {
                    sockets[tournamentId].close();
                    sockets[tournamentId] = null;
                }
            }, 2000);
        };
    }, [tournamentId]);
};

export default useTournamentSocket;
